<template>
  <material-card class="entity-history">
    <v-layout slot="header" px-3 align-content-center>
      <v-flex mt-2 pl-3 align-center justify-center>
        <div class="headline">Historia</div>

        <p>Informacje o wykonanych akcjach</p>
      </v-flex>

      <check-permission permissions="HISTORY.ADVANCED_LIST">
        <v-flex text-xs-right>
          <v-btn @click="toggleAdvancedHistory">Zaawansowana historia</v-btn>
        </v-flex>
      </check-permission>
    </v-layout>

    <material-table :headers="table.headers" :items="list" :total-count="count" @details="goToAuthor" />
  </material-card>
</template>

<script>
import { HistoryActionTypeTranslation, ProfileType } from '@witkac/kartamiejska-shared';
import escape from 'escape-html';
import moment from 'moment';
import { mapState } from 'vuex';

import MaterialCard from '@/components/material/Card';
import MaterialTable from '@/components/material/Table';
import { listMixin } from '@/mixins/list';
import { baseFilters } from '@/utils/baseFilters';

// @vuese
// Wyświetlanie zakładki historii
export default {
  components: {
    MaterialCard,
    MaterialTable
  },
  mixins: [listMixin('entityHistory', { listError: 'Wystąpił błąd podczas pobierania historii.' })],
  props: {
    // Czy pobierac historie partnera
    getPartner: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data() {
    return {
      table: {
        headers: [
          {
            value: 'created',
            text: 'Data',
            sortable: true,
            orderBy: 'created',
            render: item => moment(item.created).format('YYYY-MM-DD HH:mm')
          },
          {
            value: 'action',
            text: 'Rodzaj',
            sortable: true,
            orderBy: 'action',
            render: item => HistoryActionTypeTranslation[item.action]
          },
          {
            value: 'changes',
            text: 'Opis',
            sortable: false,
            render: item => {
              if (item.json && this.advancedHistory) {
                return this.parseJsonChangesToHTML(item);
              }

              return item.json || !item.changes ? 'BRAK' : item.changes;
            }
          },
          {
            value: 'profile',
            text: 'Autor zmian',
            sortable: false,
            link: true,
            render: item => {
              if (!item || !item.profile) {
                return '';
              }

              return `${item.profile.firstName} ${item.profile.lastName}`;
            }
          }
        ]
      },
      advancedHistory: (() => this.$route.query.advancedHistory)()
    };
  },
  computed: {
    ...mapState({
      list: state => state.entityHistory.list
    })
  },
  methods: {
    // @vuese
    // Tworzy filtry na podstawie paginacji
    filters(pagination, route) {
      pagination.entityId = route.params.id;
      return { ...pagination, tableName: this.getPartner ? 'Partner' : 'Profile' };
    },
    // @vuese
    // Przeładowanie listy
    reloadList(route) {
      const filters = this.filters(baseFilters(route), route);

      if (route.query.advancedHistory) {
        filters.advanced = true;
      }

      return this.$store
        .dispatch('entityHistory/list', filters)
        .catch(err => this.notifyError('Wystąpił błąd podczas pobierania historii.'));
    },

    // @vuese
    // Przeniesienie na profil autora zmian
    goToAuthor(historyLog) {
      const routeName = {
        [ProfileType.OFFICIAL]: 'official',
        [ProfileType.CLIENT]: 'client',
        [ProfileType.PARTNER]: 'employeeProfile'
      };

      const params = {
        id: historyLog.userId
      };

      if (historyLog.profile.type === ProfileType.PARTNER) {
        params.id = historyLog.profile.partner.id;
        params.employeeId = historyLog.userId;
      }

      this.$router.push({
        name: routeName[historyLog.profile.type],
        params
      });
    },
    // @vuese
    // Przygotowanie tablic kluczy do wyświetlenia zaawansowanej historii
    prepareChangeKeys(parsed) {
      const beforeKeys = Object.keys(parsed.before);
      const afterKeys = Object.keys(parsed.after);

      const commonKeys = beforeKeys.filter(key => afterKeys.includes(key));
      const onlyBeforeKeys = beforeKeys.filter(key => !afterKeys.includes(key));
      const onlyAfterKeys = afterKeys.filter(key => !beforeKeys.includes(key));

      return { commonKeys, onlyBeforeKeys, onlyAfterKeys };
    },
    // @vuese
    // Parsowanie tekstu do htmla komórki tabeli zaawansowanej historii
    parseText(object, key) {
      if (!object[key]) {
        return '-';
      }
      if (typeof object[key] === 'object') {
        return JSON.stringify(object[key]);
      }

      return object[key];
    },
    // @vuese
    // Przełączanie zwykłej historii i zaawansowanej
    toggleAdvancedHistory() {
      this.advancedHistory = !this.advancedHistory;
      this.$router.replace({ ...this.$route, query: { ...this.$route.query, advancedHistory: this.advancedHistory } });
    },
    parseJsonChangesToHTML(item) {
      const parsed = JSON.parse(item.changes);
      let html = `
      <table class="advanced-history-log">
        <caption>Zmiant w encji: ${item.tableName}, ${item.entityId}</caption>

        <thead>
          <tr>
            <th>Pole</th>
            
            <th>Przed zmianą</th>
            
            <th>Po zmianie</th>
          </tr>
        </thead>
        
        <tbody>
        `;
      if (!parsed.before || !parsed.after) {
        return '';
      }

      const { commonKeys, onlyBeforeKeys, onlyAfterKeys } = this.prepareChangeKeys(parsed);

      for (const key of [...commonKeys, ...onlyBeforeKeys, ...onlyAfterKeys]) {
        const beforeText = this.parseText(parsed.before, key);
        const afterText = this.parseText(parsed.after, key);
        html += `
          <tr>
            <td>${key}</td>
            
            <td>${escape(beforeText)}</td>
            
            <td>${escape(afterText)}</td>
          </tr>`;
      }

      html += '</tbody></table>';
      return { html };
    }
  }
};
</script>

<style>
.entity-history .v-datatable td.data-table-word-break {
  vertical-align: top;
  padding-top: 12px !important;
}

table.advanced-history-log,
table.advanced-history-log th,
table.advanced-history-log tr,
table.advanced-history-log td {
  padding: 2px 5px !important;
  border: 1px solid black;
  border-collapse: collapse;
  height: auto !important;
}
</style>
